import {
  handleBackInStockEmail,
  handleKlaviyoNewsletterEmail,
  handleBackInStockSMS,
  handleKlaviyoNewsletterSMS
} from 'klaviyo-utils';

import { validatePhoneNumber, validateEmail } from 'validation-utils';

class WaitlistForm extends HTMLElement {
  constructor() {
    super();
    this._bind();
    this._variantId = null;
    this._productId = null;
    this._acceptsMarketing = false;
  }

  static get observedAttributes() {
    return ['variant-id', 'product-id'];
  }

  get variantId() {
    return this._variantId;
  }

  set variantId(value) {
    this._variantId = value;
    if (value) {
      this.setAttribute('variant-id', value);
    }
  }

  get productId() {
    return this._productId;
  }

  set productId(value) {
    this._productId = value;
    if (value) {
      this.setAttribute('product-id', value);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'variant-id') {
      this._variantId = newValue;
      // Reset submit button when variant changes so user can submit for different sizes
      if (oldValue && oldValue !== newValue) {
        this._resetSubmitButton();
      }
    }
    if (name === 'product-id') {
      this._productId = newValue;
    }
  }

  connectedCallback() {
    this._queryElements();
    this._addEvents();
    this._initState();
  }

  disconnectedCallback() {
    this._removeEvents();
  }

  _bind() {
    const propertyNames = Object.getOwnPropertyNames(
      Object.getPrototypeOf(this)
    );
    propertyNames.forEach(name => {
      if (typeof this[name] === 'function') {
        this[name] = this[name].bind(this);
      }
    });
  }

  _queryElements() {
    this._textMeBtn = this.querySelector('.waitlist-text-me-btn');
    this._emailMeBtn = this.querySelector('.waitlist-email-me-btn');
    this._emailLabel = this.querySelector('.waitlist-email-label');
    this._emailInput = this.querySelector('.waitlist-email');
    this._telephoneLabel = this.querySelector('.waitlist-telephone-label');
    this._telephoneInput = this.querySelector('.waitlist-telephone');
    this._telephoneInputContainer = this.querySelector(
      '.waitlist-telephone-wrapper'
    );
    this._checkbox = this.querySelector('.waitlist-marketing');
    this._checkboxLabel = this.querySelector('.waitlist-marketing-label');
    this._smsComplianceMsg = this.querySelector('.waitlist-compliance.sms');
    this._submitButton = this.querySelector('.waitlist-submit-btn');
    this._errorMsg = this.querySelector('.waitlist-error');
    this._marketingContainer = this.querySelector(
      '.marketing-checkbox-container'
    );
  }

  _initState() {
    if (this._checkbox) {
      this._acceptsMarketing = this._checkbox.checked;
    }
    // Initialize based on which button is active
    if (this._emailMeBtn && this._emailMeBtn.classList.contains('active')) {
      this._setInputVisibility(this._emailInput, this._emailLabel, true);
      this._setInputVisibility(
        this._telephoneInput,
        this._telephoneLabel,
        false
      );
      this._telephoneInputContainer.style.display = 'none';
      this._smsComplianceMsg.style.display = 'none';
    } else if (
      this._textMeBtn &&
      this._textMeBtn.classList.contains('active')
    ) {
      this._setInputVisibility(
        this._telephoneInput,
        this._telephoneLabel,
        true
      );
      this._setInputVisibility(this._emailInput, this._emailLabel, false);
    }
  }

  _addEvents() {
    this._removeEvents();

    if (this._textMeBtn) {
      this._textMeBtn.addEventListener('click', this._handleTextMeClick);
    }
    if (this._emailMeBtn) {
      this._emailMeBtn.addEventListener('click', this._handleEmailMeClick);
    }
    if (this._submitButton) {
      this._submitButton.addEventListener('click', this._handleSubmit);
    }
    if (this._emailInput) {
      this._emailInput.addEventListener('keydown', this._handleInputKeydown);
      this._emailInput.addEventListener('input', this._hideErrorMsg);
      this._emailInput.addEventListener('change', this._hideErrorMsg);
    }
    if (this._telephoneInput) {
      this._telephoneInput.addEventListener(
        'keydown',
        this._handleInputKeydown
      );
      this._telephoneInput.addEventListener('input', this._hideErrorMsg);
      this._telephoneInput.addEventListener('change', this._hideErrorMsg);
    }
    if (this._checkbox) {
      this._checkbox.addEventListener('change', this._handleCheckboxChange);
    }
  }

  _removeEvents() {
    if (this._textMeBtn) {
      this._textMeBtn.removeEventListener('click', this._handleTextMeClick);
    }
    if (this._emailMeBtn) {
      this._emailMeBtn.removeEventListener('click', this._handleEmailMeClick);
    }
    if (this._submitButton) {
      this._submitButton.removeEventListener('click', this._handleSubmit);
    }
    if (this._emailInput) {
      this._emailInput.removeEventListener('keydown', this._handleInputKeydown);
      this._emailInput.removeEventListener('input', this._hideErrorMsg);
      this._emailInput.removeEventListener('change', this._hideErrorMsg);
    }
    if (this._telephoneInput) {
      this._telephoneInput.removeEventListener(
        'keydown',
        this._handleInputKeydown
      );
      this._telephoneInput.removeEventListener('input', this._hideErrorMsg);
      this._telephoneInput.removeEventListener('change', this._hideErrorMsg);
    }
    if (this._checkbox) {
      this._checkbox.removeEventListener('change', this._handleCheckboxChange);
    }
  }

  _setInputVisibility(input, label, visible) {
    input.style.display = visible ? 'block' : 'none';
    input.disabled = !visible;
    if (visible) {
      label.removeAttribute('aria-hidden');
    } else {
      label.setAttribute('aria-hidden', 'true');
    }
  }

  _toggleActiveBtn(targetBtn, activeBtn) {
    if (!targetBtn.classList.contains('active')) {
      targetBtn.classList.add('active');
      targetBtn.setAttribute('aria-current', 'true');
    }
    activeBtn.classList.remove('active');
    activeBtn.setAttribute('aria-current', 'false');
  }

  _handleTextMeClick(e) {
    e.preventDefault();
    e.stopPropagation();

    this._setInputVisibility(this._emailInput, this._emailLabel, false);
    this._setInputVisibility(this._telephoneInput, this._telephoneLabel, true);
    this._telephoneInputContainer.style.display = 'flex';
    this._telephoneInput.focus();
    this._hideErrorMsg();
    // Reset submit button so user can submit phone after email
    // But don't reset if in "select a size" state - variant selection will handle that
    if (!this._isSelectSizeState()) {
      this._resetSubmitButton();
    }

    if (this._acceptsMarketing) {
      this._smsComplianceMsg.style.display = 'block';
    }
    if (this._checkboxLabel.textContent.includes('email')) {
      this._checkboxLabel.textContent = this._checkboxLabel.textContent.replace(
        'email',
        'text'
      );
    }
    this._toggleActiveBtn(this._textMeBtn, this._emailMeBtn);
  }

  _handleEmailMeClick(e) {
    e.preventDefault();
    e.stopPropagation();

    this._setInputVisibility(this._telephoneInput, this._telephoneLabel, false);
    this._telephoneInputContainer.style.display = 'none';
    this._setInputVisibility(this._emailInput, this._emailLabel, true);
    this._emailInput.focus();
    this._smsComplianceMsg.style.display = 'none';
    this._hideErrorMsg();
    // Reset submit button so user can submit email after phone
    // But don't reset if in "select a size" state - variant selection will handle that
    if (!this._isSelectSizeState()) {
      this._resetSubmitButton();
    }

    if (this._checkboxLabel.textContent.includes('text')) {
      this._checkboxLabel.textContent = this._checkboxLabel.textContent.replace(
        'text',
        'email'
      );
    }
    this._toggleActiveBtn(this._emailMeBtn, this._textMeBtn);
  }

  _handleCheckboxChange() {
    this._acceptsMarketing = this._checkbox.checked;
    if (
      this._acceptsMarketing &&
      this._textMeBtn.classList.contains('active')
    ) {
      this._smsComplianceMsg.style.display = 'block';
    } else {
      this._smsComplianceMsg.style.display = 'none';
    }
  }

  _handleInputKeydown(e) {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this._submitButton.click();
    }
  }

  _showErrorMsg(inputString) {
    this._errorMsg.textContent = `The ${inputString} provided is invalid.`;
  }

  _hideErrorMsg() {
    if (this._errorMsg) {
      this._errorMsg.textContent = '';
    }
  }

  _handleWaitlistSuccess() {
    this._marketingContainer.style.display = 'none';
    this._submitButton.disabled = true;

    // Update button text while preserving SVG
    let currentHTML = this._submitButton.innerHTML;
    let newHTML = currentHTML.replace('JOIN WAITLIST', 'JOINED WAITLIST');
    this._submitButton.innerHTML = newHTML;
    this._submitButton.title = 'Joined Waitlist';

    // Make checkmark SVG visible
    if (this._submitButton.children[0]) {
      this._submitButton.children[0].style.display = 'block';
    }
  }

  _handleSubmit(e) {
    e.preventDefault();

    const variantId = this._variantId || this.getAttribute('variant-id');

    if (!variantId) {
      console.error('WaitlistForm: No variant ID set');
      return;
    }

    // Email sign up
    if (
      this._emailInput &&
      !this._emailInput.disabled &&
      this._emailInput.value !== ''
    ) {
      const validatedEmail = validateEmail(this._emailInput.value);

      if (validatedEmail) {
        handleBackInStockEmail(this._emailInput.value, variantId);

        if (this._acceptsMarketing) {
          handleKlaviyoNewsletterEmail(this._emailInput.value);
        }
        dataLayer.push({ event: 'Waitlist Joined' });
        this._handleWaitlistSuccess();
      } else {
        this._showErrorMsg('email address');
      }
      // SMS sign up
    } else if (
      this._telephoneInput &&
      !this._telephoneInput.disabled &&
      this._telephoneInput.value !== ''
    ) {
      const validatedPhone = validatePhoneNumber(this._telephoneInput.value);

      if (validatedPhone) {
        handleBackInStockSMS(validatedPhone, variantId);

        if (this._acceptsMarketing) {
          handleKlaviyoNewsletterSMS(validatedPhone);
        }
        dataLayer.push({ event: 'Waitlist Joined' });
        this._handleWaitlistSuccess();
      } else {
        this._showErrorMsg('phone number');
      }
      // No input provided
    } else {
      if (this._textMeBtn.classList.contains('active')) {
        this._showErrorMsg('phone number');
      } else {
        this._showErrorMsg('email address');
      }
    }
  }

  // Check if the submit button is in the "select a size" state
  _isSelectSizeState() {
    if (!this._submitButton) return false;
    const buttonText =
      this._submitButton.textContent || this._submitButton.innerText;
    return buttonText.toUpperCase().includes('SIZE');
  }

  // Private method to reset just the submit button state
  _resetSubmitButton() {
    if (this._submitButton) {
      this._submitButton.disabled = false;
      let currentHTML = this._submitButton.innerHTML;
      let newHTML = currentHTML.replace('JOINED WAITLIST', 'JOIN WAITLIST');
      this._submitButton.innerHTML = newHTML;
      this._submitButton.title = 'Join Waitlist';
      if (this._submitButton.children[0]) {
        this._submitButton.children[0].style.display = 'none';
      }
    }
    // Show marketing container again
    if (this._marketingContainer) {
      this._marketingContainer.style.display = 'flex';
    }
  }

  // Public method to reset the form state
  reset() {
    if (this._emailInput) {
      this._emailInput.value = '';
    }
    if (this._telephoneInput) {
      this._telephoneInput.value = '';
    }
    this._hideErrorMsg();
    this._resetSubmitButton();
  }
}

if (!customElements.get('waitlist-form')) {
  customElements.define('waitlist-form', WaitlistForm);
}
